// src/oura/components/PlanSettings.tsx
//
// When the work happens: the start date, and how long each phase runs
// (ourteam-backend#4477, #4517).
//
// Deliberately NOT the same control as Refresh. Refresh changes WHICH TASKS
// exist; this changes only their dates, and the backend guarantees the
// difference — a settings PATCH touches no recorded progress, no week move and
// no removal. Two operations that both "update the plan" have to be told apart
// by their names, or an operator avoids both.
//
// Until this existed there was no way to set a start date at all, so every plan
// ran from the day somebody first opened the To-do tab. That is why a Kickoff
// block could read "Aug 24 to Aug 24".

import { createPortal } from "react-dom";
import { useState } from "react";
import { XIcon, Loader2Icon } from "lucide-react";
import type { BusinessTracker } from "../tracker";
import { blockLabel, type TrackerBlock } from "../tracker";
import { shortDate } from "../lib/oura";
import { cn } from "../lib/cn";
import { Button } from "../primitives/Button";

/** A block's length has to be bounded somewhere. Eight weeks is longer than any
 *  phase the team has sold; the point is to reject a typo, not to have a view. */
const MIN_WEEKS = 1;
const MAX_WEEKS = 8;

type Phase = { week: number; title: string; weeks: number };

export function PlanSettings({
    tracker,
    saving,
    onSave,
    onClose,
}: {
    tracker: BusinessTracker;
    saving: boolean;
    onSave: (patch: { startDate: string; phases: { week: number; weeks: number }[] }) => void;
    onClose: () => void;
}) {
    const [startDate, setStartDate] = useState(
        tracker.generatedAt && tracker.startDate ? tracker.startDate.slice(0, 10) : tracker.startDate?.slice(0, 10) ?? "",
    );
    const [phases, setPhases] = useState<Phase[]>(() =>
        tracker.weeks.map((w) => ({ week: w.week, title: w.title, weeks: w.weeks ?? 1 })),
    );

    const bump = (week: number, by: number) =>
        setPhases((prev) =>
            prev.map((p) =>
                p.week === week
                    ? { ...p, weeks: Math.max(MIN_WEEKS, Math.min(MAX_WEEKS, p.weeks + by)) }
                    : p,
            ),
        );

    /**
     * The label a block WILL carry, computed the same way the backend computes
     * it — from position and length, never typed. Shown live so the effect of a
     * change is visible before saving rather than after.
     */
    const labelFor = (i: number): string => {
        const p = phases[i];
        if (p.week === 0) return p.title || "Kickoff";
        let first = 1;
        for (let k = 0; k < i; k++) if (phases[k].week !== 0) first += phases[k].weeks;
        const last = first + p.weeks - 1;
        return first === last ? `Week ${first}` : `Weeks ${first} to ${last}`;
    };

    const totalWeeks = phases.reduce((n, p) => n + (p.week === 0 ? 0 : p.weeks), 0);
    const dirty =
        startDate !== (tracker.startDate?.slice(0, 10) ?? "")
        || phases.some((p) => p.weeks !== (tracker.weeks.find((w) => w.week === p.week)?.weeks ?? 1));

    return createPortal(
        <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/30" onClick={onClose} />
            <div className="absolute left-1/2 top-1/2 w-full max-w-[480px] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-gray-200 bg-white p-6 shadow-xl">
                <div className="flex items-start justify-between gap-3">
                    <div>
                        <h2 className="text-lg font-semibold leading-snug text-gray-900">Plan settings</h2>
                        <p className="mt-0.5 text-sm text-gray-500">
                            Changes when the work happens. The tasks stay as they are.
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        aria-label="Close"
                        className="flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-700"
                    >
                        <XIcon className="h-4 w-4" />
                    </button>
                </div>

                <div className="mt-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    Work starts
                </div>
                <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    aria-label="Date the work starts"
                    className="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                {/* The backend says whether a person chose this date or it was
                    defaulted. Repeating its sentence rather than inventing one
                    keeps the two from disagreeing. */}
                <p className="mt-1.5 text-xs text-gray-500">
                    {tracker.startDateSource === "operator"
                        ? "Set by your team."
                        : tracker.startDateNote || "Defaulted. Weekends and Malaysian public holidays are skipped."}
                </p>

                <div className="mt-5 text-[11px] font-semibold uppercase tracking-wider text-gray-400">
                    Phases
                </div>
                <div className="mt-1.5">
                    {phases.map((p, i) => (
                        <div
                            key={p.week}
                            className={cn(
                                "flex items-center gap-3 py-2.5",
                                i > 0 && "border-t border-gray-100",
                            )}
                        >
                            <div className="min-w-0 flex-1">
                                <div className="text-sm text-gray-900">{p.title || blockLabel(p.week as TrackerBlock)}</div>
                                <div className="mt-0.5 text-xs text-gray-400">{labelFor(i)}</div>
                            </div>
                            {/* Kickoff has no length to set: it is the block before
                                the clock starts, and giving it weeks would push
                                every dated week later for no work. */}
                            {p.week === 0 ? (
                                <span className="flex-shrink-0 text-xs text-gray-400">before week 1</span>
                            ) : (
                                <div className="flex flex-shrink-0 items-center gap-1">
                                    <StepBtn label={`Shorten ${p.title}`} disabled={p.weeks <= MIN_WEEKS} onClick={() => bump(p.week, -1)}>
                                        &minus;
                                    </StepBtn>
                                    <span className="min-w-[62px] text-center text-sm tabular-nums text-gray-600">
                                        {p.weeks === 1 ? "1 week" : `${p.weeks} weeks`}
                                    </span>
                                    <StepBtn label={`Lengthen ${p.title}`} disabled={p.weeks >= MAX_WEEKS} onClick={() => bump(p.week, 1)}>
                                        +
                                    </StepBtn>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="mt-3 flex items-baseline justify-between text-sm">
                    <span className="text-gray-500">Runs for</span>
                    <span className="font-medium text-gray-900">
                        {totalWeeks === 1 ? "1 week" : `${totalWeeks} weeks`}
                        {startDate ? ` from ${shortDate(startDate)}` : ""}
                    </span>
                </div>

                <div className="mt-6 flex justify-end gap-2">
                    {/* The house Button, not hand-rolled classes. The pair here
                        was inventing its own padding and radius, which is how two
                        dialogs end up looking like two products. */}
                    <Button variant="outline" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        onClick={() => onSave({ startDate, phases: phases.map((p) => ({ week: p.week, weeks: p.weeks })) })}
                        disabled={saving || !startDate || !dirty}
                    >
                        {saving && <Loader2Icon className="h-4 w-4 animate-spin" />}
                        Save changes
                    </Button>
                </div>
            </div>
        </div>,
        document.body,
    );
}

function StepBtn({
    label,
    disabled,
    onClick,
    children,
}: {
    label: string;
    disabled: boolean;
    onClick: () => void;
    children: React.ReactNode;
}) {
    return (
        <button
            type="button"
            aria-label={label}
            disabled={disabled}
            onClick={onClick}
            className="flex h-6 w-6 items-center justify-center rounded-md border border-gray-200 text-sm text-gray-600 transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:text-gray-300"
        >
            {children}
        </button>
    );
}
